<template>
  <div class="friend-group-list">
    <div class="group-header">
      <div class="group-title">好友分组</div>
      <div class="header-actions">
        <el-button type="text" @click="onShowAddFriend">
          <i class="el-icon-user-solid"></i>
        </el-button>
        <el-button type="text" @click="onAddGroup">
          <i class="el-icon-plus"></i>
        </el-button>
      </div>
    </div>
    <div class="search-box">
      <el-input
        v-model="searchText"
        placeholder="搜索好友"
        prefix-icon="el-icon-search"
        clearable
      ></el-input>
    </div>
    <el-scrollbar style="height: calc(100vh - 250px);">
      <div v-for="group in groups" :key="group.id" class="group-item-friend">
        <div class="group-name" 
              @click="onToggleGroup(group)"
              @dragover.prevent="onGroupNameDragOver($event, group)"
              @dragleave="onGroupNameDragLeave($event)"
              @drop.prevent="onDrop($event, group)">
          <i :class="['el-icon-arrow-right', { 'is-active': group.expanded }]"></i>
          <span>{{ group.name }}</span>
          <span class="friend-count">({{ group.friends ? group.friends.length : 0 }})</span>
          <div class="group-actions">
            <el-button v-if="group.friends && group.friends.length > 0" type="text" @click.stop="onSendGroupMessage(group)" title="群发消息">
              <i class="el-icon-message"></i>
            </el-button>
            <el-button type="text" @click.stop="onEditGroup(group)">
              <i class="el-icon-edit"></i>
            </el-button>
            <el-button type="text" @click.stop="onDeleteGroup(group)">
              <i class="el-icon-delete"></i>
            </el-button>
          </div>
        </div>
        <div v-show="group.expanded" class="friend-list"
             @dragover.prevent="onDragOver($event, group)"
             @drop.prevent="onDrop($event, group)">
          <friend-item
            v-for="(friend, index) in group.friends"
            :key="friend.id"
            v-show="isMatchSearch(friend)"
            :friend="friend"
            :index="index"
            :active="friend === $store.state.friendStore.activeFriend"
            @chat="onSendMessage(friend)"
            @delete="onDelItem(friend, index)"
            @click.native="onActiveItem(friend, index)"
            draggable="true"
            @dragstart="onDragStart($event, friend, group)"
          />
        </div>
      </div>
    </el-scrollbar>

    <!-- 添加/编辑分组对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="400px">
      <el-form :model="groupForm" :rules="rules" ref="groupForm" label-width="80px">
        <el-form-item label="分组名称" prop="name">
          <el-input v-model="groupForm.name" maxlength="20" show-word-limit></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="onSubmitGroup">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 添加好友对话框 -->
    <add-friend :dialogVisible="showAddFriend" @close="onCloseAddFriend"></add-friend>
    
    <!-- 移动好友对话框 -->
    <move-friend-dialog
      :visible.sync="moveDialogVisible"
      :friend-id="draggedFriend ? draggedFriend.id : null"
      @success="onMoveSuccess"
    />

    <!-- 群发消息对话框 -->
    <el-dialog :title="groupMessageTitle" :visible.sync="groupMessageVisible" width="500px" :close-on-click-modal="false">
      <div v-if="selectedGroup" class="group-message-info">
        <p><strong>分组：</strong>{{ selectedGroup.name }}</p>
        <p><strong>好友数量：</strong>{{ selectedGroup.friends ? selectedGroup.friends.length : 0 }} 人</p>
        <div v-if="selectedGroup.friends && selectedGroup.friends.length > 0" class="friend-preview">
          <strong>将发送给：</strong>
          <div class="friend-tags">
            <el-tag v-for="friend in selectedGroup.friends" :key="friend.id" size="mini" style="margin: 2px;">
              {{ friend.remarkName || friend.nickName }}
            </el-tag>
          </div>
        </div>
      </div>
      
      <!-- 发送进度显示 -->
      <div v-if="sendingGroupMessage && sendProgress.total > 0" class="send-progress">
        <el-progress 
          :percentage="Math.round((sendProgress.completed / sendProgress.total) * 100)"
          :status="sendProgress.failed > 0 ? 'exception' : 'success'"
        >
        </el-progress>
        <p class="progress-text">
          正在发送消息... {{ sendProgress.completed }}/{{ sendProgress.total }}
          <span v-if="sendProgress.failed > 0" style="color: #f56c6c;">
            (失败: {{ sendProgress.failed }})
          </span>
        </p>
      </div>
      
      <el-form :model="groupMessageForm" :rules="groupMessageRules" ref="groupMessageForm" label-width="80px">
        <el-form-item label="消息内容" prop="content">
          <el-input
            v-model="groupMessageForm.content"
            type="textarea"
            :rows="4"
            placeholder="请输入要发送的消息内容"
            maxlength="500"
            show-word-limit
            :disabled="sendingGroupMessage"
          ></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="onCancelGroupMessage" :disabled="sendingGroupMessage">取 消</el-button>
        <el-button type="primary" @click="onSubmitGroupMessage" :loading="sendingGroupMessage">
          {{ sendingGroupMessage ? '发送中...' : '发送消息' }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import FriendItem from './FriendItem.vue'
import AddFriend from './AddFriend.vue'
import MoveFriendDialog from './MoveFriendDialog.vue'
import imageCache from '@/utils/imageCache'

export default {
  name: 'FriendGroupList',
  components: {
    FriendItem,
    AddFriend,
    MoveFriendDialog
  },
  data() {
    return {
      groups: [],
      dialogVisible: false,
      dialogTitle: '添加分组',
      searchText: '',
      showAddFriend: false,
      moveDialogVisible: false,
      draggedFriend: null,
      sourceGroup: null,
      groupForm: {
        id: null,
        name: '',
        sort: 0
      },
      rules: {
        name: [
          { required: true, message: '请输入分组名称', trigger: 'blur' },
          { max: 20, message: '长度不能超过20个字符', trigger: 'blur' }
        ]
      },
      groupMessageVisible: false,
      groupMessageTitle: '群发消息',
      selectedGroup: null,
      groupMessageForm: {
        content: ''
      },
      groupMessageRules: {
        content: [
          { required: true, message: '请输入消息内容', trigger: 'blur' },
          { max: 500, message: '消息内容不能超过500个字符', trigger: 'blur' }
        ]
      },
      sendingGroupMessage: false,
      sendProgress: {
        total: 0,
        completed: 0,
        failed: 0
      }
    }
  },
  created() {
    this.loadGroups()
  },
  methods: {
    /**
     * 保存分组展开状态到本地存储
     */
    saveExpandedState() {
      if (!this.groups || this.groups.length === 0) return
      
      try {
        const expandedStates = {}
        this.groups.forEach(group => {
          if (group && group.id) {
            expandedStates[group.id] = group.expanded || false
          }
        })
        
        localStorage.setItem('friendGroupExpandedStates', JSON.stringify(expandedStates))
      } catch (error) {
        console.warn('保存分组展开状态失败:', error)
      }
    },
    
    /**
     * 从本地存储加载分组展开状态
     */
    loadExpandedStates() {
      try {
        const savedStates = localStorage.getItem('friendGroupExpandedStates')
        if (savedStates) {
          return JSON.parse(savedStates)
        }
      } catch (error) {
        console.warn('加载分组展开状态失败:', error)
      }
      return {}
    },
    
    async loadGroups() {
      try {
        // 从本地存储加载分组展开状态
        const expandedStates = this.loadExpandedStates()
        
        // 保存当前分组展开状态（优先级高于本地存储）
        const currentStates = {}
        if (this.groups && this.groups.length > 0) {
          this.groups.forEach(group => {
            if (group.id) {
              currentStates[group.id] = group.expanded || false
            }
          })
        }
        
        const res = await this.$http.get('/friend/group/list')
        console.log('API Response:', res)
        this.groups = res.map(group => ({
          ...group,
          // 恢复展开状态：优先使用当前状态，其次使用本地存储，否则默认折叠
          expanded: (currentStates[group.id] !== undefined) ? 
                    currentStates[group.id] : 
                    (expandedStates[group.id] || false)
        }))
        console.log('Processed groups:', this.groups)
        
        // 更新 store 中的好友数据
        const allFriends = this.groups.reduce((acc, group) => {
          if (group.friends) {
            acc.push(...group.friends)
          }
          return acc
        }, [])
        this.$store.commit('setFriends', allFriends)

        // 预加载好友头像
        this.preloadFriendAvatars(allFriends)
      } catch (error) {
        this.$message.error('加载分组失败')
      }
    },
    onToggleGroup(group) {
      group.expanded = !group.expanded
      // 保存展开状态到本地存储
      this.saveExpandedState()
    },
    onAddGroup() {
      this.dialogTitle = '添加分组'
      this.groupForm = {
        id: null,
        name: '',
        sort: 0
      }
      this.dialogVisible = true
    },
    onEditGroup(group) {
      this.dialogTitle = '编辑分组'
      this.groupForm = {
        id: group.id,
        name: group.name,
        sort: group.sort
      }
      this.dialogVisible = true
    },
    async onDeleteGroup(group) {
      try {
        await this.$confirm('确定要删除该分组吗？', '提示', {
          type: 'warning'
        })
        await this.$http.post(`/friend/group/delete/${group.id}`)
        this.$message.success('删除成功')
        this.loadGroups()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    },
    async onSubmitGroup() {
      try {
        await this.$refs.groupForm.validate()
        const url = this.groupForm.id ? '/friend/group/modify' : '/friend/group/create'
        await this.$http.post(url, this.groupForm)
        this.$message.success(this.groupForm.id ? '修改成功' : '添加成功')
        this.dialogVisible = false
        this.loadGroups()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error(this.groupForm.id ? '修改失败' : '添加失败')
        }
      }
    },
    onSendMessage(friend) {
      this.$emit('chat', friend)
    },
    onDelItem(friend, index) {
      this.$emit('delete', friend, index)
    },
    onActiveItem(friend, index) {
      this.$emit('select-friend', friend)
    },
    onShowAddFriend() {
      this.showAddFriend = true
    },
    onCloseAddFriend() {
      this.showAddFriend = false
      this.loadGroups()
    },
    isMatchSearch(friend) {
      if (!this.searchText) return true
      const searchLower = this.searchText.toLowerCase()
      const searchName = friend.remarkName || friend.nickName
      return searchName && searchName.toLowerCase().includes(searchLower)
    },
    // 检查分组是否有匹配的好友
    hasMatchingFriends(group) {
      if (!this.searchText || !group.friends) return false
      return group.friends.some(friend => this.isMatchSearch(friend))
    },
    // 自动展开有匹配好友的分组
    autoExpandGroups() {
      if (this.searchText) {
        // 有搜索文本时，展开有匹配好友的分组
        this.groups.forEach(group => {
          if (this.hasMatchingFriends(group)) {
            group.expanded = true
          }
        })
      }
    },
    
    /**
     * 预加载好友头像
     * @param {Array} friends 好友列表
     */
    async preloadFriendAvatars(friends) {
      try {
        // 提取所有头像URL
        const avatarUrls = friends
          .map(friend => friend.headImage || friend.headImageThumb)
          .filter(url => url && url.trim()) // 过滤空值
          .slice(0, 20) // 只预加载前20个，避免一次性加载太多
        
        if (avatarUrls.length > 0) {
          console.log(`开始预加载 ${avatarUrls.length} 个好友头像`)
          
          // 异步预加载，不阻塞UI
          setTimeout(async () => {
            const results = await imageCache.preloadImages(avatarUrls)
            const successCount = results.filter(result => result.status === 'fulfilled').length
            console.log(`预加载完成：${successCount}/${avatarUrls.length} 个头像缓存成功`)
            
            // 输出缓存统计信息
            const stats = imageCache.getCacheStats()
            console.log('图片缓存统计:', stats)
          }, 1000) // 延迟1秒执行，让页面先渲染
        }
      } catch (error) {
        console.warn('预加载好友头像失败:', error)
      }
    },
    
    /**
     * 拖拽开始事件
     */
    onDragStart(event, friend, group) {
      try {
        // 清理上次可能存在的状态
        this.draggedFriend = null
        this.sourceGroup = null
        
        // 设置新的拖拽状态
        this.draggedFriend = friend
        this.sourceGroup = group
        
        if (event && event.dataTransfer) {
          event.dataTransfer.effectAllowed = 'move'
          // 设置拖拽数据 - 包含完整的friend和group对象
          const dragData = {
            friend: {
              id: friend.id,
              nickName: friend.nickName,
              remarkName: friend.remarkName,
              headImage: friend.headImage
            },
            sourceGroup: {
              id: group.id,
              name: group.name
            }
          }
          // 存储完整数据
          event.dataTransfer.setData('application/json', JSON.stringify(dragData))
          console.log('拖拽开始成功', dragData)
        } else {
          console.warn('拖拽开始失败：event.dataTransfer不可用')
        }
      } catch (error) {
        console.error('拖拽开始出错:', error)
      }
    },
    
    /**
     * 拖拽经过分组时的事件
     */
    onDragOver(event, group) {
      event.preventDefault()
      // 添加视觉反馈
      if (event && event.currentTarget) {
        event.currentTarget.classList.add('drop-target')
      }
      return false
    },
    
    /**
     * 拖拽经过分组名称时的事件
     */
    onGroupNameDragOver(event, group) {
      event.preventDefault()
      // 添加视觉反馈
      if (event && event.currentTarget) {
        event.currentTarget.classList.add('drop-target-group')
        
        // 如果分组未展开，延迟自动展开
        if (!group.expanded) {
          setTimeout(() => {
            if (event.currentTarget && event.currentTarget.classList.contains('drop-target-group')) {
              group.expanded = true
              // 保存展开状态到本地存储
              this.saveExpandedState()
            }
          }, 1000) // 1秒后自动展开
        }
      }
      
      return false
    },
    
    /**
     * 拖拽离开分组名称时的事件
     */
    onGroupNameDragLeave(event) {
      if (event && event.currentTarget) {
        event.currentTarget.classList.remove('drop-target-group')
      }
    },
    
    /**
     * 拖拽放置事件
     */
    async onDrop(event, targetGroup) {
      event.preventDefault()
      
      // 安全地移除类名
      if (event && event.currentTarget) {
        event.currentTarget.classList.remove('drop-target')
        event.currentTarget.classList.remove('drop-target-group')
      }
      
      try {
        let draggedFriend = this.draggedFriend
        let sourceGroup = this.sourceGroup
        
        // 如果组件状态丢失，尝试从dataTransfer恢复数据
        if (!draggedFriend && event.dataTransfer) {
          try {
            const jsonData = event.dataTransfer.getData('application/json')
            if (jsonData) {
              const dragData = JSON.parse(jsonData)
              draggedFriend = dragData.friend
              sourceGroup = dragData.sourceGroup
              console.log('从dataTransfer恢复的数据:', dragData)
              
              // 恢复组件状态
              this.draggedFriend = draggedFriend
              this.sourceGroup = sourceGroup
            }
          } catch (e) {
            console.error('解析拖拽数据失败:', e)
          }
        }
        
        // 确保有拖拽的好友和目标分组
        if (!draggedFriend || !targetGroup) {
          console.log('缺少拖拽好友或目标分组', { 
            friend: draggedFriend, 
            targetGroup,
            dataTransferTypes: event.dataTransfer ? Array.from(event.dataTransfer.types) : []
          })
          return
        }
        
        // 如果是同一个分组，不做任何操作
        if (sourceGroup && sourceGroup.id === targetGroup.id) {
          console.log('相同分组，不做操作', { sourceId: sourceGroup?.id, targetId: targetGroup.id })
          return
        }
        
        console.log('准备移动好友', { 
          draggedFriend,
          targetGroup
        })
        
        // 确保draggedFriend是个完整对象
        if (typeof draggedFriend.id !== 'undefined') {
          // 直接使用API移动好友
          try {
            // 获取好友和目标分组ID
            const friendId = parseInt(draggedFriend.id)
            const groupId = parseInt(targetGroup.id)
            
            console.log('执行移动操作:', { friendId, groupId })
            
            // 记住目标分组ID，用于稍后自动展开
            const targetGroupId = targetGroup.id
            
            // 调用API
            await this.$http.post('/friend/group/move', null, {
              params: {
                friendId,
                groupId
              }
            })
            
            this.$message.success('好友移动成功')
            
            // 刷新分组列表
            await this.loadGroups()
            
            // 确保目标分组展开显示
            const updatedTargetGroup = this.groups.find(g => g.id === targetGroupId)
            if (updatedTargetGroup) {
              updatedTargetGroup.expanded = true
              // 保存展开状态到本地存储
              this.saveExpandedState()
            }
          } catch (err) {
            console.error('移动好友API调用失败:', err)
            this.$message.error('移动失败，请稍后重试')
          }
        } else {
          this.$message.error('好友数据无效，无法移动')
        }
      } catch (error) {
        console.error('处理拖放操作时出错:', error)
        this.$message.error('拖放操作失败')
      } finally {
        // 清理拖拽状态
        this.draggedFriend = null
        this.sourceGroup = null
      }
    },
    
    /**
     * 移动成功后的回调
     */
    onMoveSuccess() {
      try {
        console.log('好友移动成功，刷新数据')
        // 刷新分组列表
        this.loadGroups()
      } catch (error) {
        console.error('移动成功后刷新数据出错:', error)
      } finally {
        // 清理拖拽状态
        this.draggedFriend = null
        this.sourceGroup = null
      }
    },

    /**
     * 打开群发消息对话框
     */
    onSendGroupMessage(group) {
      this.selectedGroup = group
      this.groupMessageForm.content = ''
      this.groupMessageVisible = true
      this.sendProgress = { total: 0, completed: 0, failed: 0 } // 重置进度
    },

    /**
     * 提交群发消息
     */
    async onSubmitGroupMessage() {
      try {
        await this.$refs.groupMessageForm.validate()
        if (!this.selectedGroup) {
          this.$message.error('请选择一个分组')
          return
        }

        const friends = this.selectedGroup.friends || []
        if (friends.length === 0) {
          this.$message.warning('该分组没有好友，无法发送消息')
          return
        }

        this.sendingGroupMessage = true
        this.sendProgress.total = friends.length
        this.sendProgress.completed = 0
        this.sendProgress.failed = 0
        
        // 逐个发送消息，实时更新进度
        for (let i = 0; i < friends.length; i++) {
          const friend = friends[i]
          try {
            const msgInfo = {
              sendId: this.$store.state.userStore.userInfo.id,  // 发送者ID
              recvId: friend.id,                  // 接收者ID
              sendNickName: this.$store.state.userStore.userInfo.nickName,
              recvNickName: friend.remarkName || friend.nickName,
              content: this.groupMessageForm.content,
              contentType: 0,  // 文本消息
              type: 0,         // 普通消息
              sendTime: new Date().getTime(),
              selfSend: true,  // 标记为自己发送的消息
              status: 1        // 消息状态：已发送
            }
            
            const response = await this.$http.post('/message/private/send', msgInfo)
            
            // 发送成功后更新消息ID
            msgInfo.id = response.id
            
            // 创建或更新聊天会话
            const chatInfo = {
              targetId: friend.id,
              type: 'PRIVATE',
              showName: friend.remarkName || friend.nickName,
              headImage: friend.headImage || friend.headImageThumb
            }
            
            // 打开聊天会话（如果不存在会自动创建）
            this.$store.commit('openChat', chatInfo)
            
            // 将消息插入到聊天中
            this.$store.commit('insertMessage', [msgInfo, chatInfo])
            
            this.sendProgress.completed++
            
            // 添加短暂延迟，避免过快发送导致服务器压力
            if (i < friends.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 200))
            }
          } catch (error) {
            console.error('发送消息失败:', friend.nickName, error)
            this.sendProgress.failed++
          }
        }
        
        // 显示最终结果
        if (this.sendProgress.failed === 0) {
          this.$message.success(`群发消息发送成功！已向 ${this.sendProgress.completed} 位好友发送消息`)
        } else if (this.sendProgress.completed > 0) {
          this.$message.warning(`部分消息发送成功：成功 ${this.sendProgress.completed} 条，失败 ${this.sendProgress.failed} 条`)
        } else {
          this.$message.error('群发消息发送失败，请稍后重试')
        }
        
        this.groupMessageVisible = false
        this.selectedGroup = null
      } catch (error) {
        if (error !== 'cancel') {
          console.error('群发消息失败:', error)
          this.$message.error('群发消息发送失败')
        }
      } finally {
        this.sendingGroupMessage = false
      }
    },

    onCancelGroupMessage() {
      this.groupMessageVisible = false
      this.sendingGroupMessage = false
      this.sendProgress = { total: 0, completed: 0, failed: 0 }
    }
  },
  watch: {
    searchText: {
      handler(newVal, oldVal) {
        // 当搜索文本变化时，自动展开有匹配好友的分组
        this.autoExpandGroups()
      },
      immediate: false
    }
  }
}
</script>

<style lang="scss" scoped>
.friend-group-list {
  height: 100%;
  background-color: #fff;

  .group-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 20px;
    border-bottom: 1px solid #eee;

    .group-title {
      font-size: 16px;
      font-weight: bold;
    }

    .header-actions {
      display: flex;
      align-items: center;

      .el-button {
        padding: 2px 5px;
      }
    }
  }

  .search-box {
    padding: 10px 20px;
    border-bottom: 1px solid #eee;
  }

  .group-item-friend {
    .group-name {
      display: flex;
      align-items: center;
      padding: 10px 20px;
      cursor: pointer;
      transition: background-color 0.3s;
      // position: relative;

      &:hover {
        background-color: #f5f7fa;

        .group-actions {
          display: block;
        }
      }
      
      &.drop-target-group {
        background-color: #e6f1fc;
        border: 1px dashed #409eff;
      }

      i {
        margin-right: 10px;
        transition: transform 0.3s;

        &.is-active {
          transform: rotate(90deg);
        }
      }

      .friend-count {
        margin-left: 5px;
        color: #909399;
        font-size: 12px;
      }

      .group-actions {
        position: absolute;
        right: 20px;
        display: none;

        .el-button {
          padding: 2px 5px;
        }
      }
    }

    .friend-list {
      background-color: #f5f7fa;
      min-height: 5px; /* 确保空分组也能拖入 */
      transition: background-color 0.3s;
      
      &.drop-target {
        background-color: #e6f1fc;
        border: 1px dashed #409eff;
      }
    }
  }
}

.group-message-info {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 4px;
  
  p {
    margin: 8px 0;
    color: #606266;
    
    strong {
      color: #303133;
    }
  }
  
  .friend-preview {
    margin-top: 12px;
    
    .friend-tags {
      margin-top: 8px;
      max-height: 120px;
      overflow-y: auto;
      
      .el-tag {
        margin: 2px 4px 2px 0;
      }
    }
  }
}

.send-progress {
  margin-bottom: 20px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  text-align: center;

  .el-progress {
    margin-bottom: 8px;
  }

  .progress-text {
    font-size: 14px;
    color: #606266;
    span {
      margin-left: 10px;
    }
  }
}
</style>
